import { useEffect, useMemo, useState } from 'react';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { useLocation, useNavigate } from 'react-router-dom';

import { ZB_BANKING_SUNSET_PH3 } from '#client/constants/experimentsFlags';
import { EARLY_EXPIRATION_MINUTES } from '#client/constants/time';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useCheckTransactionContext from '#client/hooks/useCheckTransactionContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { BANKING_TRANSITION_SEGMENT } from '#client/pages/BankingTransition/constants';

import ABTestRedirectContainer from './ABTestRedirectContainer';
import NavigateModals from './components/NavigateModals';

export default function Dashboard() {
  const { pathname } = useLocation();
  const [loadingNavigate, setLoadingNavigate] = useState(true);
  const { isOpeningModal } = useCheckOpenModal();
  const bankingPath = useBankingPath();

  const properties = useMemo(
    () => ({
      customDestinationPageName_: 'account_funding_test_dashboard',
      appPageName: 'account_funding_test_dashboard',
      appSection: 'banking',
      experienceOrigin: 'dashboard'
    }),
    []
  );

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { isInvalidStatus, appStatusError } = useApplicationStatusContext();
  const {
    isEmptyUnitAccountId,
    error: unitAccountIdError,
    isEnabled,
    isEnrolled,
    isFetchingMfaStatus,
    isValidMfa,
    loading: loadingUnitAccountId,
    mfaError
  } = useUnitAccountId();

  // prefetch mobile check deposit token
  useCustomerToken({
    type: TOKEN_TYPES.MOBILE_CHECK_DEPOSITS,
    prefetch: true,
    earlyExpirationMinutes: EARLY_EXPIRATION_MINUTES
  });

  const invalidMFA = isEnabled === false || isEnrolled === false || isValidMfa === false;
  const dashboardNotFundedUrl = `${bankingPath}/dashboard-notfunded`;
  const navigate = useNavigate();

  const {
    error: transactionError,
    isEmptyTransaction,
    hasTransactionOrPayments,
    loading: loadingTransaction
  } = useCheckTransactionContext();
  const [stopRedirectDashboardNotFunded, setStopRedirectDashboardNotFunded] = useSessionStorage(
    `stop_redirect_dashboard_notfunded.${businessEntityUuid}`,
    false
  );

  const [isZbBankingSunsetPh3Enabled, isLoadingFlag] = useFeatureFlag(ZB_BANKING_SUNSET_PH3);
  const transitionFlowUrl = `${bankingPath}/${BANKING_TRANSITION_SEGMENT}`;

  const error = appStatusError || unitAccountIdError || transactionError || mfaError;
  const loading =
    loadingUnitAccountId || loadingTransaction || isFetchingMfaStatus || isLoadingFlag;
  useEffect(() => {
    if (
      !loading &&
      !error &&
      isEmptyTransaction &&
      stopRedirectDashboardNotFunded === false &&
      !isOpeningModal
    ) {
      if (isZbBankingSunsetPh3Enabled) {
        // The transition flow is served by this MFE's own router, so it's an in-router
        // navigate. Guard against exitToDashboard() looping back here by setting the same
        // flag DashboardNotFunded's exits use.
        setStopRedirectDashboardNotFunded(true);
        navigate(transitionFlowUrl, { replace: true });
      } else {
        navigate(dashboardNotFundedUrl);
      }
    }
  }, [
    loading,
    error,
    isEmptyTransaction,
    isZbBankingSunsetPh3Enabled,
    stopRedirectDashboardNotFunded,
    navigate,
    transitionFlowUrl,
    dashboardNotFundedUrl,
    isOpeningModal,
    setStopRedirectDashboardNotFunded
  ]);

  useEffect(() => {
    if (
      !loading &&
      (hasTransactionOrPayments ||
        isInvalidStatus ||
        isEmptyUnitAccountId ||
        stopRedirectDashboardNotFunded ||
        invalidMFA ||
        isOpeningModal ||
        error)
    ) {
      setLoadingNavigate(false);
    }
  }, [
    loading,
    hasTransactionOrPayments,
    isInvalidStatus,
    isEmptyUnitAccountId,
    stopRedirectDashboardNotFunded,
    invalidMFA,
    isOpeningModal,
    error
  ]);

  if (loadingNavigate) {
    return (
      <>
        <PageViewedTracker
          {...properties}
          key={pathname}
        />
        <PageLoading />
      </>
    );
  }

  return (
    <>
      <PageViewedTracker
        {...properties}
        key={pathname}
      />
      {isOpeningModal ? <NavigateModals /> : <ABTestRedirectContainer />}
    </>
  );
}
